import { CheckCircleIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import { CopyableTextField } from '../../../components/input/copyable-text-field.tsx'
import * as styles from './integration-drawer.css.ts'
import {
  getWebhookHandoffSteps,
  getWebhookUrlHelperText,
  UNPROTECTED_WEBHOOK_HELPER_TEXT,
  WEBHOOK_URL_COPY_LABEL,
  WEBHOOK_URL_LABEL,
  type WebhookHandoffSubject,
} from './integration-webhook.ts'

export type WebhookHandoffProps = {
  /** The catalog label, e.g. "HCL AppScan" — what the success line and the toast both name. */
  toolLabel: string
  instanceName: string
  webhookUrl: string
  /** The tool, and the one fact about the saved instance its own steps turn on. */
  subject: WebhookHandoffSubject
  /** Said when the deployment also has an admin-console connection whose URL differs from this one. */
  systemConnectionNote?: string
}

/**
 * What the drawer shows after an instance that receives webhooks is created: the instance is saved
 * and already usable for pulls, and what remains is a configuration the customer performs in a tool
 * or provider Pixee does not administer.
 */
export const WebhookHandoff: React.FC<WebhookHandoffProps> = ({
  toolLabel,
  instanceName,
  webhookUrl,
  subject,
  systemConnectionNote,
}) => {
  const steps = getWebhookHandoffSteps(subject)
  const isWebhookUnprotected = match(subject)
    .with({ type: 'sonar' }, ({ hasWebhookSecret }) => !hasWebhookSecret)
    .with({ type: 'appscan' }, { type: 'github' }, { type: 'gitlab' }, { type: 'azure' }, () => false)
    .exhaustive()
  const deliveryNoun = match(subject)
    .with({ type: 'sonar' }, { type: 'appscan' }, () => 'results')
    .with({ type: 'github' }, { type: 'gitlab' }, { type: 'azure' }, () => 'webhook deliveries')
    .exhaustive()

  return (
    <div className={styles.section}>
      <p className={styles.handoffSuccessLine}>
        <CheckCircleIcon size={20} weight="fill" className={styles.handoffSuccessIcon} />
        <span>
          <strong>
            {toolLabel} {instanceName}
          </strong>{' '}
          is connected. Finish setup in {toolLabel} so it can send {deliveryNoun} to Pixee.
        </span>
      </p>
      <CopyableTextField
        id="webhook-url"
        label={WEBHOOK_URL_LABEL}
        value={webhookUrl}
        copyLabel={WEBHOOK_URL_COPY_LABEL}
        helperText={getWebhookUrlHelperText(toolLabel)}
        secondaryHelperText={isWebhookUnprotected ? UNPROTECTED_WEBHOOK_HELPER_TEXT : undefined}
      />
      <ol className={styles.handoffSteps}>
        {steps.map(step => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {systemConnectionNote !== undefined && <p className={styles.handoffNote}>{systemConnectionNote}</p>}
    </div>
  )
}
